import React, { useState } from 'react';
import { Logger } from '../../utils/logger';
import { notifyError } from '../../services/notificationService';

const log = Logger.create('ExportTab');

/**
 * ExportTab Component
 * 
 * Simple export functionality for quiz data
 * Features:
 * - CSV export (participants + scores)
 * - Detailed CSV export (individual answers per user)
 */
function ExportTab({ instance, scores, questionStats, participants, answers }) {
  const [exporting, setExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState('');

  // Export to CSV (Statistics)
  const exportToCSV = () => {
    setExporting(true);
    setExportSuccess('');

    try {
      // CSV Header
      const headers = ['Rang', 'Name', 'Punkte', 'Richtig', 'Beantwortet', 'Fragen gesamt', 'Beigetreten'];
      
      // CSV Rows
      const rows = scores.map((participant, index) => [
        index + 1,
        participant.name,
        participant.isGamification ? `${participant.score} Pkt` : `${participant.score}%`,
        participant.correctCount,
        participant.totalAnswered,
        participant.totalQuestions,
        new Date(participant.joinedAt).toLocaleString('de-DE')
      ]);

      // Convert to CSV string
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // Create download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `${instance.name || 'quiz'}_ergebnisse_${Date.now()}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setExportSuccess('CSV erfolgreich exportiert!');
      setTimeout(() => setExportSuccess(''), 3000);
    } catch (error) {
      log.error('Fehler beim CSV-Export', error);
      notifyError('Fehler beim CSV-Export: ' + error.message);
    } finally {
      setExporting(false);
    }
  };

  // Export detailed CSV with answers per user
  const exportDetailedCSV = () => {
    setExporting(true);
    setExportSuccess('');

    try {
      // CSV Header - Name, Score, then each question's answer and correctness
      const headers = [
        'Name',
        'Punkte',
        'Richtig',
        'Beigetreten',
        ...instance.questions.map((q, i) => `F${i + 1} Antwort`),
        ...instance.questions.map((q, i) => `F${i + 1} Status`)
      ];
      
      // CSV Rows
      const rows = scores.map((participant) => {
        // Get answers for this participant from the answers structure
        // answers[questionIndex][participantId] = { selectedIndex, selectedIndices, ... }
        const getParticipantAnswer = (questionIndex) => {
          const questionAnswers = answers[questionIndex] || {};
          return questionAnswers[participant.participantId];
        };

        const answerValues = instance.questions.map((q, i) => {
          const answer = getParticipantAnswer(i);
          if (!answer) return 'Keine Antwort';
          
          // Support both single and multiple choice
          const selectedIndices = answer.selectedIndices || 
            (answer.selectedIndex !== null && answer.selectedIndex !== undefined ? [answer.selectedIndex] : []);
          
          if (selectedIndices.length === 0) return 'Keine Antwort';
          
          return selectedIndices.map(idx => {
            const option = q.options?.[idx];
            return option ? `${String.fromCharCode(65 + idx)}` : '?';
          }).join(', ');
        });

        const statusValues = instance.questions.map((q, i) => {
          const answer = getParticipantAnswer(i);
          if (!answer) return '-';
          
          const selectedIndices = answer.selectedIndices || 
            (answer.selectedIndex !== null && answer.selectedIndex !== undefined ? [answer.selectedIndex] : []);
          
          if (selectedIndices.length === 0) return '-';
          
          // Get correct answer indices
          const correctIndices = q.correctAnswerIndices || 
            (q.correctAnswer !== undefined ? [q.correctAnswer] : [0]);
          
          // Check if correct
          const isCorrect = selectedIndices.length === correctIndices.length &&
            correctIndices.every(idx => selectedIndices.includes(idx));
          
          return isCorrect ? 'Richtig' : 'Falsch';
        });

        return [
          participant.name,
          participant.isGamification ? `${participant.score} Pkt` : `${participant.score}%`,
          participant.correctCount,
          new Date(participant.joinedAt).toLocaleString('de-DE'),
          ...answerValues,
          ...statusValues
        ];
      });

      // Convert to CSV string
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // Create download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `${instance.name || 'quiz'}_detailliert_${Date.now()}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setExportSuccess('Detailliertes CSV erfolgreich exportiert!');
      setTimeout(() => setExportSuccess(''), 3000);
    } catch (error) {
      log.error('Fehler beim CSV-Export', error);
      notifyError('Fehler beim CSV-Export: ' + error.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      {/* Success Message */}
      {exportSuccess && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg flex items-center gap-2 text-green-700 dark:text-green-400">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          {exportSuccess}
        </div>
      )}

      {/* Export Options - Simple 2 column grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* CSV Export Card */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-md transition-shadow bg-white dark:bg-gray-800">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-success-100 dark:bg-success-900/30 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-success-600 dark:text-success-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">CSV Export</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Teilnehmerpunkte und Statistiken als CSV herunterladen.
              </p>
              <button
                onClick={exportToCSV}
                disabled={exporting || scores.length === 0}
                className="px-4 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
              >
                {exporting ? 'Exportiere...' : 'CSV herunterladen'}
              </button>
            </div>
          </div>
        </div>

        {/* Detailed CSV Export Card */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-md transition-shadow bg-white dark:bg-gray-800">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-primary-600 dark:text-primary-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 2a2 2 0 00-2 2v8a2 2 0 002 2h6a2 2 0 002-2V6.414A2 2 0 0016.414 5L14 2.586A2 2 0 0012.586 2H9z" />
                <path d="M3 8a2 2 0 012-2v10h8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Detailliertes CSV</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Alle Antworten pro Teilnehmer mit Richtig/Falsch-Status.
              </p>
              <button
                onClick={exportDetailedCSV}
                disabled={exporting || scores.length === 0}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
              >
                {exporting ? 'Exportiere...' : 'Detailliertes CSV herunterladen'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExportTab;
